/**
 * The completed step of the Production Line journey (production-lines):
 * the delivered video (or its placeholder while the file is not yet
 * served), its facts, the download and the next actions. Moved verbatim
 * out of ProductionLineDetailPage.tsx.
 */

import { useState, type ReactNode } from 'react';

import type { MerchantCreativeRunView } from '../../merchant/api.js';
import type { WorkspaceCopy } from '../../merchant/copy.js';
import { formatDate, formatDuration } from '../../merchant/model.js';
import {
  Button,
  ButtonLink,
  DefinitionList,
  DownloadIcon,
  Eyebrow,
  Pix,
  Poster,
} from '../../merchant/ui/primitives.js';

export function CompleteStep({
  copy,
  language,
  workspaceName,
  lineTitle,
  lineId,
  run,
  canMakeAnother,
  busy,
  onAnotherVersion,
}: {
  readonly copy: WorkspaceCopy;
  readonly language: 'en' | 'ar';
  readonly workspaceName: string;
  readonly lineTitle: string;
  readonly lineId: string;
  readonly run: MerchantCreativeRunView;
  readonly canMakeAnother: boolean;
  readonly busy: boolean;
  readonly onAnotherVersion: () => void;
}): ReactNode {
  const video = run.video;
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const unavailable = video === null || failedUrl === video.videoUrl;
  const card = run.card;
  const duration = formatDuration(
    video?.durationSeconds ?? card?.durationSeconds ?? null,
  );
  return (
    <div className="mj-ws-done">
      {!unavailable ? (
        <video
          className="mj-ws-video"
          controls
          preload="metadata"
          poster={video.posterUrl}
          src={video.videoUrl}
          onError={() => {
            setFailedUrl(video.videoUrl);
          }}
        />
      ) : (
        <Poster state="placeholder" duration={duration} label={copy.line.previewSoon} />
      )}
      <section aria-labelledby="dn-h">
        <Eyebrow>
          <Pix status="complete" />
          {copy.line.videoReady}
        </Eyebrow>
        <h2 id="dn-h" className="mj-ws-h2 mj-ws-mt-8">
          {card?.hookLine ?? card?.direction ?? lineTitle}
        </h2>
        <DefinitionList
          className="mj-ws-mt-20"
          columns="wide"
          rows={[
            [copy.line.workspace, workspaceName],
            [copy.line.productionLine, lineTitle],
            [copy.line.direction, card?.direction ?? copy.common.dash],
            [
              copy.line.duration,
              <span key="d" className="mj-ws-tnum">
                {duration ?? copy.common.dash}
              </span>,
            ],
            [copy.line.language, card?.language ?? copy.common.dash],
            [
              copy.line.format,
              `${card?.aspectRatio ?? copy.common.dash} · ${card?.targetPlatforms.join(' · ') ?? copy.common.dash}`,
            ],
            [
              copy.line.completed,
              <span key="c" className="mj-ws-tnum">
                {video?.completedAt
                  ? formatDate(video.completedAt, language)
                  : copy.common.dash}
              </span>,
            ],
          ]}
        />
        {failedUrl !== null && (
          <p role="alert">
            {language === 'ar'
              ? 'تعذّر تشغيل الملف. يمكنك إعادة تحميل الصفحة أو تجربة رابط التنزيل.'
              : 'The video could not be played. Reload the page or try the download link.'}
          </p>
        )}
        {video !== null && !video.notified ? (
          <p className="mj-ws-muted mj-ws-sm mj-ws-mt-12">{copy.line.notNotified}</p>
        ) : null}
        {video?.limitations.map((limitation) => (
          <p key={limitation} className="mj-ws-muted mj-ws-sm mj-ws-mt-8">
            <strong>{copy.line.note}:</strong> {limitation}
          </p>
        ))}
        <div className="mj-ws-row mj-ws-mt-24">
          {video !== null ? (
            <a className="mj-ws-btn mj-ws-btn-p" href={video.videoUrl} download>
              <DownloadIcon />
              {copy.line.download}
            </a>
          ) : (
            <Button variant="primary" disabled>
              <DownloadIcon />
              {copy.line.download}
            </Button>
          )}
          <Button disabled={busy} onClick={onAnotherVersion}>
            {canMakeAnother ? copy.line.makeAnother : copy.line.anotherVersion}
          </Button>
          <ButtonLink to={`/videos/line/${encodeURIComponent(lineId)}`}>
            {copy.line.viewInVideos}
          </ButtonLink>
          <ButtonLink to="/production-lines" variant="quiet">
            {copy.line.returnToLines}
          </ButtonLink>
        </div>
      </section>
    </div>
  );
}
